import { useEffect, useState, useRef } from 'react';
import InputBox from '../../../components/InputBox';
import Accordion from 'react-bootstrap/Accordion';
import { getTempUserDetailsById } from '../../../services/users/tempUserDetails';
import { getGlobalOptions } from '../../../services/auth/selfSignUp';
import { convertForSelect } from '../../../utils/convertApiData';
import { enumGlobalOptionData, enumGlobalOptionDataMap } from '../../../utils/enumGlobalOptionData';
import { selfSignUpUserData } from '../../../utils/selfSignUpUserData';
import { toast } from '../../../components/AppToast';
import { postReviewRevision } from '../../../services/admin/admin';

/** API may return contact as an array, a single object, or a keyed map of contact objects. */
const normalizeContacts = (contact) => {
  if (contact == null) return [];
  if (Array.isArray(contact)) return contact.filter(Boolean);
  if (typeof contact === 'object') {
    const values = Object.values(contact);
    if (
      values.length > 0 &&
      values.every((v) => v && typeof v === 'object' && !Array.isArray(v))
    ) {
      return values;
    }
    return [contact];
  }
  return [];
};

const editableFieldNames = [
//     'tenant_type', 'tenant_city', 'tenant_title_1' ,"tenant_firstName_1","tenant_lastName_1","tenant_firstName_2", "tenant_lastName_2"
//   ,"tenant_regDetails_doc_type_1"
];

const dropdownFields = [
  'tenant_industryType',
  'tenant_RegDocuments',
  'tenant_type',
  'tenant_title',
  'tenant_country',
  'tenant_subscriptionModel',
];
const checkboxFields = ['tenant_systemCapabilities'];

const TenantsManage = ({ id, onCancel }) => {
  const [formData, setFormData] = useState({});
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [checkboxOptions, setCheckboxOptions] = useState({});
  const [docTypeOptions, setDocTypeOptions] = useState([]); // For DOC_TYPE dropdown
  // State for new document upload
  const [newDoc, setNewDoc] = useState({ doc_type: '', file: null, name: '' }); // For adding new document
  const fileInputRef = useRef(null);
  const [needChangeSection, setNeedChangeSection] = useState(null);
  const [needChangeFields, setNeedChangeFields] = useState([]);
  const [needChangeComment, setNeedChangeComment] = useState('');
  const [approveSection, setApproveSection] = useState(null);
  const [approveFields, setApproveFields] = useState([]);
  const [approveComment, setApproveComment] = useState('');

  // Store all status and value objects for final submit
  const [allStatusArr, setAllStatusArr] = useState([]);
  // Store approve/need-change state per section
  const [sectionApproveState, setSectionApproveState] = useState({}); // { section: { fields: [], comment: '' } }
  const [sectionNeedChangeState, setSectionNeedChangeState] = useState({}); // { section: { fields: [], comment: '' } }

  const getDropdownData = async (key) => {
    try {
      const data = await getGlobalOptions(enumGlobalOptionData?.[key]);
      return convertForSelect(data);
    } catch (err) {
      toast.error(err);
      return [];
    }
  };

  const fetchAllDropdownOptions = async () => {
    const ddOptions = {};
    const cbOptions = {};
    const exceptKeys = ['tenant_country', 'tenant_subscriptionModel'];
    for (const field of dropdownFields) {
      if (!exceptKeys.includes(field)) {
        let enumKey = enumGlobalOptionDataMap[field];
        ddOptions[field] = await getDropdownData(enumKey);
      }
    }
    for (const field of checkboxFields) {
      let enumKey = enumGlobalOptionDataMap[field];
      cbOptions[field] = await getDropdownData(enumKey);
    }
    setCheckboxOptions(cbOptions);
    setDropdownOptions(ddOptions);
  };

 const fetchDocTypes = async()=>{
      let enumKey = enumGlobalOptionDataMap["tenant_RegDocuments"];
      const options = await getDropdownData(enumKey);
      setDocTypeOptions(options);
  }

  useEffect(() => {
    fetchAllDropdownOptions();
    getUserData();
    fetchDocTypes();
    // eslint-disable-next-line
  }, []);

  const getUserData = async () => {
    try {
      const data = await getTempUserDetailsById(id);
      const contact = normalizeContacts(data?.contact);
      // Merge address fields into formData for flat access
      const flatData = {
        ...data,
        contact,
        ...(data.address || {})
      };
      setFormData(flatData);
    } catch (err) {
      console.log(err, 'error');
    }
  };

  const handleChange = (section, field, value, idx) => {
    if (section === 'contact') {
      const updatedContact = [...(formData.contact || [])];
      updatedContact[idx][field] = value;
      setFormData({ ...formData, contact: updatedContact });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const renderFields = (fields, section = null, idx = null) => {
    return Object.keys(fields).map((key) => {
      if (
        Array.isArray(fields[key]) &&
        key !== 'contact' &&
        key !== 'tenant_regDetails' &&
        !checkboxFields.includes(key) &&
        !dropdownFields.includes(key)
      ) return null;
      if (typeof fields[key] === 'object' && fields[key] !== null && !Array.isArray(fields[key])) return null;
      if (key === 'contact') return null;
      let isEditable = editableFieldNames.includes(key);
      if (section === 'contact' && idx !== null) {
        // Check for keys like 'tenant_title_1', 'tenant_title_2', etc.
        const indexedKey = `${key}_${idx + 1}`;
        isEditable = editableFieldNames.includes(indexedKey);
      }
      const label = section === 'contact' && selfSignUpUserData.contact
        ? selfSignUpUserData.contact[Object.keys(selfSignUpUserData.contact).find(k => k.toLowerCase() === key.toLowerCase())] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        : (selfSignUpUserData[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
      // Dynamic column size for dropdowns
      let dropdownColClass = 'col-md-3';
       if (['tenant_title'].includes(key)) {
        dropdownColClass = 'col-md-2';
      }
      if (dropdownFields.includes(key)) {
        return (
          <div className={dropdownColClass} key={key}>
            <label className="form-label fw-semibold small">
              {label}
              {isEditable && <span className="required">*</span>}
            </label>
            <select
              className="form-control"
              value={fields[key] || ''}
              onChange={isEditable ? (e => handleChange(section, key, e.target.value, idx)) : undefined}
              disabled={!isEditable}
              required={isEditable}
            >
              <option value="">Select</option>
              {(dropdownOptions[key] || []).map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        );
      }
      if (checkboxFields.includes(key)) {
        return (
          <div className="field-row" key={key} style={{ width: '100%' }}>
            <label className="form-label fw-semibold small mb-2">
              {label}
              {isEditable && <span className="required">*</span>}
            </label>
            <div className="d-flex flex-wrap flex-column">
              {(checkboxOptions[key] || []).map(opt => (
                <div key={opt.value} className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`${key}_${opt.value}`}
                    value={opt.value}
                    checked={Array.isArray(fields[key]) && fields[key].includes(opt.value)}
                    onChange={isEditable ? (e => {
                      let newValue = Array.isArray(fields[key]) ? [...fields[key]] : [];
                      if (e.target.checked) {
                        newValue.push(opt.value);
                      } else {
                        newValue = newValue.filter(v => v !== opt.value);
                      }
                      handleChange(section, key, newValue, idx);
                    }) : undefined}
                    disabled={!isEditable}
                  />
                  <label className="form-check-label" htmlFor={`${key}_${opt.value}`}>{opt.label}</label>
                </div>
              ))}
            </div>
          </div>
        );
      }
      // Render single checkbox for boolean fields
      if (typeof fields[key] === 'boolean') {
        return (
          <div className="col-md-12 d-flex align-items-center" key={key}>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id={`${key}_checkbox_${idx ?? ''}`}
                checked={fields[key]}
                onChange={isEditable ? (e => handleChange(section, key, e.target.checked, idx)) : undefined}
                disabled={!isEditable}
              />
              <label className="input-label d-block ms-2" htmlFor={`${key}_checkbox_${idx ?? ''}`}>{label}</label>
            </div>
          </div>
        );
      }
      // Handle tenant_regDetails as a special case in renderFields
      if (key === 'tenant_regDetails' && Array.isArray(fields[key])) {
        const editMode = editableFieldNames.includes('tenant_regDetails_doc_type_1');
        return (
          <div className="col-12" key={key}>
            {editMode && (
              <div className="row mb-3 gap-4">
                <div className="col-md-4">
                  <label className="form-label">Select Document Type</label>
                  <select
                    className="form-control"
                    value={newDoc.doc_type || ''}
                    onChange={e => setNewDoc({ ...newDoc, doc_type: e.target.value })}
                  >
                    <option value="">Select</option>
                    {docTypeOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Select Document File</label>
                  <input
                    type="file"
                    className="form-control"
                    accept={
                      newDoc.doc_type === 'image' ? 'image/*' :
                      newDoc.doc_type === 'pdf' ? 'application/pdf' :
                      newDoc.doc_type === 'doc' ? '.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document' : '*'
                    }
                    ref={fileInputRef}
                    onChange={e => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = ev => {
                          setNewDoc({ ...newDoc, file: ev.target.result, name: file.name });
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                </div>
                <div className="col-md-2 d-flex align-items-end">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      if (newDoc.doc_type && newDoc.file && newDoc.name) {
                        const updatedDocs = Array.isArray(fields[key]) ? [...fields[key], { ...newDoc }] : [{ ...newDoc }];
                        handleChange(null, key, updatedDocs);
                        setNewDoc({ doc_type: '', file: null, name: '' });
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      } else {
                        toast.error('Please select document type and file.');
                      }
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
            {/* Uploaded Documents List */}
            <div className="row mb-3 gap-4">
              <div className="col-md-4 mb-3" key={idx}>
                <h6>Uploaded Documents</h6>
                <ul>
                  {fields[key]?.map((doc, idx2) => (
                    <li key={idx2} className="d-flex align-items-center gap-2">
                      <span>{doc.name} ({doc.doc_type})</span>
                      {editableFieldNames.includes('tenant_regDetails_doc_type_1') && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => {
                            const updatedDocs = fields[key].filter((_, i) => i !== idx2);
                            handleChange(null, key, updatedDocs);
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );
      }
      return (
        <InputBox
          key={key}
          label={label}
          value={fields[key]}
          onChange={isEditable ? (e) => handleChange(section, key, e.target.value, idx) : undefined}
          readOnly={!isEditable}
          isRequired={isEditable}
          labelClassName="input-label"
        />
      );
    });
  };

  // When opening approve/need-change, load from state
  const handleApprove = (section) => {
    setApproveSection(section);
    setApproveFields(sectionApproveState[section]?.fields || []);
    setApproveComment(sectionApproveState[section]?.comment || '');
  };

  const handleApproveSubmit = (section) => {
    const fields = approveFields;
    const statusArr = fields.map(field => ({
      field,
      label: getFieldLabel(field, section),
      status: 'approved',
      section,
      remark: approveComment
    }));
    setAllStatusArr(prev => [...prev, ...statusArr]);
    setSectionApproveState(prev => ({
      ...prev,
      [section]: { fields, comment: approveComment }
    }));
    setApproveSection(null);
    setApproveFields([]);
    setApproveComment('');
  };

  const handleNeedChanges = (section, fields) => {
    setNeedChangeSection(section);
    setNeedChangeFields(sectionNeedChangeState[section]?.fields || []);
    setNeedChangeComment(sectionNeedChangeState[section]?.comment || '');
  };

  const handleNeedChangeSubmit = (section, fieldsInSection) => {
    const fields = needChangeFields;
    const statusArr = fields.map(field => ({
      field,
      label: getFieldLabel(field, section),
      status: 'need_change',
      section,
      remark: needChangeComment
    }));
    setAllStatusArr(prev => [...prev, ...statusArr]);
    setSectionNeedChangeState(prev => ({
      ...prev,
      [section]: { fields, comment: needChangeComment }
    }));
    setNeedChangeSection(null);
    setNeedChangeFields([]);
    setNeedChangeComment('');
  };

  // Helper: get all reviewable fields
  const getAllReviewableFields = () => {
    let fields = [];
    [
      'Welcome',
      'Identification',
      'Address',
      'Contact',
      'System Information',
      'Payment & Submit'
    ].forEach(section => {
      if (section === 'Contact') {
        const list = normalizeContacts(formData?.contact);
        if (list.length > 0) {
          Object.keys(list[0]).forEach(field => fields.push({ section, field }));
        }
      } else {
        getSectionFields(section).forEach(field => fields.push({ section, field }));
      }
    });
    return fields;
  };

  const isAllReviewed = () => {
    const allFields = getAllReviewableFields();
    const reviewMap = {};
    allStatusArr.forEach(item => {
      reviewMap[item.field] = item;
    });
    for (const { field } of allFields) {
      const review = reviewMap[field];
      if (!review) return false;
      if (review.status === 'need_change' && (!review.remark || !review.remark.trim())) return false;
    }
    return true;
  };

  const handleFinalSubmit = async () => {
    if (!isAllReviewed()) {
      toast.error('All fields must be approved or rejected, and rejected fields require a comment.');
      return;
    }
    const tenantId = id;
    const overallRemark = "";
    const fieldReviews = allStatusArr.map((item, idx) => ({
      fieldPath: item.field,
      isApproved: item.status === 'approved',
      remark: item.remark || ''
    }));
    const payload = {
      tenantId,
      overallRemark,
      fieldReviews
    };
    try {
      await postReviewRevision(payload);
      toast.success('Review submitted successfully!');
    } catch (err) {
      toast.error('Error submitting review: ' + err);
      console.error(err);
    }
  };

  const getSectionFields = (section) => {
    switch (section) {
      case 'Welcome':
        return ['tenant_type'];
      case 'Identification':
        return ['tenant_name', 'tenant_industryType', 'tenant_regDetails'];
      case 'Address':
        return ['tenant_address1', 'tenant_city', 'tenant_state', 'tenant_postCode', 'tenant_country'];
      case 'Contact': {
        const list = normalizeContacts(formData?.contact);
        return list.length > 0 ? Object.keys(list[0]) : [];
      }
      case 'System Information':
        return ['tenant_wmsSystemInUse', 'tenant_erpSystemInUse', 'tenant_integrationRequired', 'tenant_numberOfUserLicenses', 'tenant_systemCapabilities'];
      case 'Payment & Submit':
        return ['tenant_subscriptionModel', 'tenant_paymentDetails'];
      default:
        return [];
    }
  };

  const getFieldLabel = (field, section = null) => {
    const isContactSection = String(section || '').toLowerCase() === 'contact';
    if (isContactSection && selfSignUpUserData.contact) {
      return (
        selfSignUpUserData.contact[
          Object.keys(selfSignUpUserData.contact).find(k => k.toLowerCase() === field.toLowerCase())
        ] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      );
    }
    return (
      selfSignUpUserData[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    );
  };

  const contactList = normalizeContacts(formData?.contact);

  return (
    <div className="tenants-manage-panel black-white-theme">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <div>
          <p className="mb-0 fw-bold fs-5 tenants-manage-panel__title">Tenant details</p>
          <p className="mb-0 small text-muted">Review sections and submit when all fields are processed.</p>
        </div>
        {typeof onCancel === 'function' && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
            onClick={onCancel}
          >
            <i className="bi bi-arrow-left" aria-hidden />
            Back to list
          </button>
        )}
      </div>
      {/* <div className="temp-user-details black-white-theme container mt-4 p-4 rounded shadow"> */}
        <Accordion defaultActiveKey="-1" alwaysOpen className="custom-accordion tenants-manage-accordion">
          <Accordion.Item eventKey="0">
            <Accordion.Header>Welcome</Accordion.Header>
            <Accordion.Body>
              <div className="row mb-3 gap-4">
                {renderFields({ tenant_type: formData.tenant_type })}
              </div>
              <div className="d-flex gap-2 mt-2">
                <button onClick={() => handleApprove('Welcome')} className="btn btn-success btn-sm">Approve</button>
                <button onClick={() => handleNeedChanges('Welcome')} className="btn btn-warning btn-sm">Need Changes</button>
              </div>
              <SectionReviewBox
                section="Welcome"
                approveSection={approveSection}
                needChangeSection={needChangeSection}
                approveFields={approveFields}
                setApproveFields={setApproveFields}
                approveComment={approveComment}
                setApproveComment={setApproveComment}
                needChangeFields={needChangeFields}
                setNeedChangeFields={setNeedChangeFields}
                needChangeComment={needChangeComment}
                setNeedChangeComment={setNeedChangeComment}
                getSectionFields={getSectionFields}
                getFieldLabel={getFieldLabel}
                handleApproveSubmit={handleApproveSubmit}
                handleNeedChangeSubmit={handleNeedChangeSubmit}
                setApproveSection={setApproveSection}
                setNeedChangeSection={setNeedChangeSection}
              />
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="1">
            <Accordion.Header>Identification</Accordion.Header>
            <Accordion.Body>
              <div className="row mb-3 gap-4">
                {renderFields({ tenant_name: formData.tenant_name, tenant_industryType: formData.tenant_industryType ,tenant_regDetails:formData.tenant_regDetails })}
              </div>
              <div className="d-flex gap-2 mt-2">
                <button onClick={() => handleApprove('Identification')} className="btn btn-success btn-sm">Approve</button>
                <button onClick={() => handleNeedChanges('Identification')} className="btn btn-warning btn-sm">Need Changes</button>
              </div>
              <SectionReviewBox
                section="Identification"
                approveSection={approveSection}
                needChangeSection={needChangeSection}
                approveFields={approveFields}
                setApproveFields={setApproveFields}
                approveComment={approveComment}
                setApproveComment={setApproveComment}
                needChangeFields={needChangeFields}
                setNeedChangeFields={setNeedChangeFields}
                needChangeComment={needChangeComment}
                setNeedChangeComment={setNeedChangeComment}
                getSectionFields={getSectionFields}
                getFieldLabel={getFieldLabel}
                handleApproveSubmit={handleApproveSubmit}
                handleNeedChangeSubmit={handleNeedChangeSubmit}
                setApproveSection={setApproveSection}
                setNeedChangeSection={setNeedChangeSection}
              />
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="2">
            <Accordion.Header>Address</Accordion.Header>
            <Accordion.Body>
              <div className="row mb-3 gap-4">
                {renderFields({ tenant_address1: formData.tenant_address1, tenant_city: formData.tenant_city, tenant_state: formData.tenant_state, tenant_postCode: formData.tenant_postCode, tenant_country: formData.tenant_country })}
              </div>
              <div className="d-flex gap-2 mt-2">
                <button onClick={() => handleApprove('Address')} className="btn btn-success btn-sm">Approve</button>
                <button onClick={() => handleNeedChanges('Address')} className="btn btn-warning btn-sm">Need Changes</button>
              </div>
              <SectionReviewBox
                section="Address"
                approveSection={approveSection}
                needChangeSection={needChangeSection}
                approveFields={approveFields}
                setApproveFields={setApproveFields}
                approveComment={approveComment}
                setApproveComment={setApproveComment}
                needChangeFields={needChangeFields}
                setNeedChangeFields={setNeedChangeFields}
                needChangeComment={needChangeComment}
                setNeedChangeComment={setNeedChangeComment}
                getSectionFields={getSectionFields}
                getFieldLabel={getFieldLabel}
                handleApproveSubmit={handleApproveSubmit}
                handleNeedChangeSubmit={handleNeedChangeSubmit}
                setApproveSection={setApproveSection}
                setNeedChangeSection={setNeedChangeSection}
              />
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="3">
            <Accordion.Header>Contact</Accordion.Header>
            <Accordion.Body>
              {contactList.length === 0 && (
                <p className="text-muted small mb-0">No contact records on this tenant.</p>
              )}
              {contactList.map((contactRow, idx) => (
                <div className="contact-item" key={idx}>
                  <div className="row mb-3 gap-4">
                    <h5 className="col-md-12 fw-bold mb-0">Contact {idx + 1}</h5>
                    {renderFields(contactRow, 'contact', idx)}
                    <div className="col-md-12" />
                  </div>
                </div>
              ))}
              <div className="d-flex gap-2 mt-2">
                <button onClick={() => handleApprove('Contact')} className="btn btn-success btn-sm">Approve</button>
                <button onClick={() => handleNeedChanges('Contact')} className="btn btn-warning btn-sm">Need Changes</button>
              </div>
              <SectionReviewBox
                section="Contact"
                approveSection={approveSection}
                needChangeSection={needChangeSection}
                approveFields={approveFields}
                setApproveFields={setApproveFields}
                approveComment={approveComment}
                setApproveComment={setApproveComment}
                needChangeFields={needChangeFields}
                setNeedChangeFields={setNeedChangeFields}
                needChangeComment={needChangeComment}
                setNeedChangeComment={setNeedChangeComment}
                getSectionFields={getSectionFields}
                getFieldLabel={getFieldLabel}
                handleApproveSubmit={handleApproveSubmit}
                handleNeedChangeSubmit={handleNeedChangeSubmit}
                setApproveSection={setApproveSection}
                setNeedChangeSection={setNeedChangeSection}
              />
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="4">
            <Accordion.Header>System Information</Accordion.Header>
            <Accordion.Body>
              <div className="row mb-3 gap-4">
                {renderFields({ tenant_wmsSystemInUse: formData.tenant_wmsSystemInUse, tenant_erpSystemInUse: formData.tenant_erpSystemInUse, tenant_integrationRequired: formData.tenant_integrationRequired, tenant_numberOfUserLicenses: formData.tenant_numberOfUserLicenses })}
                {renderFields({ tenant_systemCapabilities: formData.tenant_systemCapabilities })}
              </div>
              <div className="d-flex gap-2 mt-2">
                <button onClick={() => handleApprove('System Information')} className="btn btn-success btn-sm">Approve</button>
                <button onClick={() => handleNeedChanges('System Information')} className="btn btn-warning btn-sm">Need Changes</button>
              </div>
              <SectionReviewBox
                section="System Information"
                approveSection={approveSection}
                needChangeSection={needChangeSection}
                approveFields={approveFields}
                setApproveFields={setApproveFields}
                approveComment={approveComment}
                setApproveComment={setApproveComment}
                needChangeFields={needChangeFields}
                setNeedChangeFields={setNeedChangeFields}
                needChangeComment={needChangeComment}
                setNeedChangeComment={setNeedChangeComment}
                getSectionFields={getSectionFields}
                getFieldLabel={getFieldLabel}
                handleApproveSubmit={handleApproveSubmit}
                handleNeedChangeSubmit={handleNeedChangeSubmit}
                setApproveSection={setApproveSection}
                setNeedChangeSection={setNeedChangeSection}
              />
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="5">
            <Accordion.Header>Payment & Submit</Accordion.Header>
            <Accordion.Body>
              <div className="row mb-3 gap-4">
                {renderFields({ tenant_subscriptionModel: formData.tenant_subscriptionModel })}
                {renderFields({ tenant_paymentDetails: formData.tenant_paymentDetails })}
              </div>
              <div className="d-flex gap-2 mt-2">
                <button onClick={() => handleApprove('Payment & Submit')} className="btn btn-success btn-sm">Approve</button>
                <button onClick={() => handleNeedChanges('Payment & Submit')} className="btn btn-warning btn-sm">Need Changes</button>
              </div>
              <SectionReviewBox
                section="Payment & Submit"
                approveSection={approveSection}
                needChangeSection={needChangeSection}
                approveFields={approveFields}
                setApproveFields={setApproveFields}
                approveComment={approveComment}
                setApproveComment={setApproveComment}
                needChangeFields={needChangeFields}
                setNeedChangeFields={setNeedChangeFields}
                needChangeComment={needChangeComment}
                setNeedChangeComment={setNeedChangeComment}
                getSectionFields={getSectionFields}
                getFieldLabel={getFieldLabel}
                handleApproveSubmit={handleApproveSubmit}
                handleNeedChangeSubmit={handleNeedChangeSubmit}
                setApproveSection={setApproveSection}
                setNeedChangeSection={setNeedChangeSection}
              />
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
        <div className="d-flex gap-2 mt-3 flex-wrap">
          <button type="button" onClick={handleFinalSubmit} className="btn btn-primary px-4">
            Submit review
          </button>
        </div>
    </div>
  );
};

// Reusable checkbox for field selection
export const FieldCheckbox = ({ field, checked, onChange, label, idPrefix }) => (
  <div className="form-check" key={field}>
    <input
      className="form-check-input"
      type="checkbox"
      id={`${idPrefix}_${field}`}
      checked={checked}
      onChange={onChange}
    />
    <label className="form-check-label" htmlFor={`${idPrefix}_${field}`}>{label}</label>
  </div>
);

// Reusable SectionReviewBox for Approve/Need Change UI
export const SectionReviewBox = ({
  section,
  approveSection,
  needChangeSection,
  approveFields,
  setApproveFields,
  approveComment,
  setApproveComment,
  needChangeFields,
  setNeedChangeFields,
  needChangeComment,
  setNeedChangeComment,
  getSectionFields,
  getFieldLabel,
  handleApproveSubmit,
  handleNeedChangeSubmit,
  setApproveSection,
  setNeedChangeSection
}) => (
  (approveSection === section || needChangeSection === section) && (
    <div className="mt-3 section-review-box border-0 rounded-3 p-3 d-flex gap-4 flex-wrap">
      {/* Approve UI */}
      <div style={{ flex: 1, minWidth: 250, display: approveSection === section ? 'block' : 'none' }}>
        <div className="mb-2 fw-bold">Select fields that are approved:</div>
        {getSectionFields(section).map(field => (
          <FieldCheckbox
            key={field}
            field={field}
            checked={approveFields.includes(field)}
            onChange={e => {
              if (e.target.checked) {
                setApproveFields([...approveFields, field]);
              } else {
                setApproveFields(approveFields.filter(f => f !== field));
              }
            }}
            label={getFieldLabel(field, section)}
            idPrefix={`approve_${section.replace(/\s/g, '')}`}
          />
        ))}
        <div className="mt-2">
          <label className="form-label">Comment (optional):</label>
          <textarea
            className="form-control w-auto"
            value={approveComment}
            onChange={e => setApproveComment(e.target.value)}
          />
        </div>
        <div className="d-flex gap-2 mt-2">
          <button className="btn btn-primary btn-sm" onClick={() => handleApproveSubmit(section)}>Submit</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setApproveSection(null)}>Cancel</button>
        </div>
      </div>
      {/* Need Change UI */}
      <div style={{ flex: 1, minWidth: 250, display: needChangeSection === section ? 'block' : 'none' }}>
        <div className="mb-2 fw-bold">Select fields that need changes:</div>
        {getSectionFields(section).map(field => (
          <FieldCheckbox
            key={field}
            field={field}
            checked={needChangeFields.includes(field)}
            onChange={e => {
              if (e.target.checked) {
                setNeedChangeFields([...needChangeFields, field]);
              } else {
                setNeedChangeFields(needChangeFields.filter(f => f !== field));
              }
            }}
            label={getFieldLabel(field, section)}
            idPrefix={`needchange_${section.replace(/\s/g, '')}`}
          />
        ))}
        <div className="mt-2">
          <label className="form-label">Comment (optional):</label>
          <textarea
            className="form-control w-auto"
            value={needChangeComment}
            onChange={e => setNeedChangeComment(e.target.value)}
          />
        </div>
        <div className="d-flex gap-2 mt-2">
          <button className="btn btn-primary btn-sm" onClick={() => handleNeedChangeSubmit(section, getSectionFields(section))}>Submit</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setNeedChangeSection(null)}>Cancel</button>
        </div>
      </div>
    </div>
  )
);

export default TenantsManage;
